import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

export type PushMultiSearchOption = {
  value: string;
  label: string;
  hint?: string;
  group?: string;
};

type Props = {
  values: string[];
  onChange: (values: string[]) => void;
  options: PushMultiSearchOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
};

const PushMultiSearchSelect: React.FC<Props> = ({
  values,
  onChange,
  options,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  disabled,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(
    () => options.filter((o) => values.includes(o.value)),
    [options, values]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const hay = `${o.label} ${o.value} ${o.hint || ''} ${o.group || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [options, query]);

  const grouped = useMemo(() => {
    const groups = new Map<string, PushMultiSearchOption[]>();
    for (const opt of filtered) {
      const key = opt.group || '';
      const list = groups.get(key) || [];
      list.push(opt);
      groups.set(key, list);
    }
    return Array.from(groups.entries());
  }, [filtered]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = (value: string) => {
    if (values.includes(value)) onChange(values.filter((v) => v !== value));
    else onChange([...values, value]);
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
          setQuery('');
        }}
        className="flex w-full min-h-[42px] items-center justify-between gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-left text-sm dark:border-gray-600 dark:bg-gray-900 disabled:opacity-50"
      >
        <div className="flex min-w-0 flex-1 flex-wrap gap-1">
          {selected.length === 0 ? (
            <span className="text-gray-400">{placeholder}</span>
          ) : (
            selected.map((o) => (
              <span
                key={o.value}
                className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-950/40 dark:text-blue-200"
              >
                {o.label}
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(o.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      toggle(o.value);
                    }
                  }}
                  className="rounded-full hover:bg-blue-100 dark:hover:bg-blue-900"
                >
                  <XMarkIcon className="h-3 w-3" />
                </span>
              </span>
            ))
          )}
        </div>
        <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-400" />
      </button>
      {open ? (
        <div className="absolute z-[80] mt-1 w-full min-w-[260px] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-900">
          <div className="flex items-center gap-1.5 border-b px-2 py-1.5 dark:border-gray-700">
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full bg-transparent py-1 text-sm outline-none dark:text-white"
            />
          </div>
          <ul className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-gray-400">No matches</li>
            ) : (
              grouped.map(([group, items]) => (
                <li key={group || '_all'}>
                  {group ? (
                    <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                      {group}
                    </p>
                  ) : null}
                  {items.map((o) => {
                    const checked = values.includes(o.value);
                    return (
                      <button
                        key={o.value}
                        type="button"
                        className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-blue-50 dark:hover:bg-blue-950/40 ${
                          checked ? 'bg-blue-50 font-medium dark:bg-blue-950/30' : ''
                        }`}
                        onClick={() => toggle(o.value)}
                      >
                        <span
                          className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[10px] ${
                            checked
                              ? 'border-blue-600 bg-blue-600 text-white'
                              : 'border-gray-300 dark:border-gray-600'
                          }`}
                        >
                          {checked ? '✓' : ''}
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate text-gray-900 dark:text-white">{o.label}</span>
                          {o.hint || o.value !== o.label ? (
                            <span className="block truncate text-[10px] text-gray-400">{o.hint || o.value}</span>
                          ) : null}
                        </span>
                      </button>
                    );
                  })}
                </li>
              ))
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

export default PushMultiSearchSelect;
